import { saveAs } from 'file-saver';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { trackSegment } from '~/components/SegmentAnalytics';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { usePageSidebarContext } from '~/eds/src/components/PageSidebar/PageSidebarContext';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { Tab } from '~/eds/src/types/Tab';
import { UserAction } from '~/eds/src/types/UserAction';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { DeleteActionModal } from '~/features/document-handler/actions/DeleteActionModal';
import { RenameActionModal } from '~/features/document-handler/actions/RenameActionModal';
import { Field } from '~/features/document-handler/types/fields';
import { GroupMembership } from '~/features/groups/Panels/GroupMembership';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { ClauseContent } from '~/types/Clause';
import { PilotId } from '~/types/PilotId';
import { SearchTextMatch } from '~/types/Search';
import { openInNewTab } from '~/utils/browser';

import Clauses from './Clauses/Clauses';
import { Fields } from './Fields/Fields';
import { SearchMatches } from './SearchMatches/SearchMatches';

export const DOCUMENT_DETAILS_PANEL_WIDTH = '560px';

type ActionConfigs<S = unknown> = {
  /** callback called when clicking on the header action. */
  onClick?: () => void;
  disabled?: boolean;
  hidden?: boolean;
  /** callback will be called when the default action (e.g. download or rename) has succeeded. if default action is overriden
   * by `onClick` callback, this will not be triggered. */
  onSuccess?: (param?: S) => void;
};

interface Props {
  actionsConfig?: {
    /** flag to override configure download action. */
    download?: ActionConfigs;
    /** Flag to indicate if navigation to document is available */
    navigate?: ActionConfigs;
    /** callback to show the next document. If not provided, action will be hidden */
    next?: ActionConfigs;
    /** callback to next document action. If not provided, action will be hidden */
    previous?: ActionConfigs;
    /** callback to delete document action. If not provided, action will be hidden */
    delete?: ActionConfigs;
    /** callback to rename document action. If not provided, action will be hidden */
    rename?: ActionConfigs<string>;
    /** callback to upload document action. If not provided, action will be hidden */
    upload?: ActionConfigs<File>;
    /** callback to view ticket action. If not provided, action will be hidden */
    viewTicket?: ActionConfigs;
  };
  /** When provided, will enable field editing. Defaults to true. */
  enableFieldEditing?: boolean;
  /** When provided, will highlight the field value. */
  activeFieldId?: EdsPilotId;
  /** hide all actions. Takes precedence over `actionsConfig` */
  hideAllActions?: boolean;
  /** When provided, will enable the Keyword Matches tab. */
  searchKeyworkMatches?: SearchTextMatch;
  /** When provided, will expand the accordion item of the clause and highlight the title */
  featuredClauses?: string[];
  /** document handler id or all duplicate handler ids. */
  docHandlerId?: PilotId | PilotId[];
  /** Panel title */
  documentName?: string;
  /** if true, the Panel will be used embedded in a SideBar. */
  isEmbedded?: boolean;
  /** When provided and enableMaximize is true, the panel expands to this width on maximize. */
  expandWidth?: string | number;
  /** callback will be called when hiding the panel.*/
  onHide: () => void;
  /** When provided, will enable Field highlighting */
  onFieldHighlight?: (highlights: Field) => void;
  /** When provided, will enable clause highlighting */
  onClauseHighlight?: (clause: ClauseContent) => void;
  /** Custom tabs to add to the panel */
  customTabs?: Tab[];
  /** When provided, will show the groups tab */
  showGroupsTab?: boolean;
  /** When provided, will show the current group in the groups tab */
  currentGroupId?: string;
}

const enabledByDefault: ActionConfigs = { hidden: false };
type Tabs = 'fields' | 'clauses' | 'matches' | 'groups';
export const DocumentDetailsPanel = ({
  actionsConfig,
  activeFieldId,
  docHandlerId: docHandlerIdParam,
  documentName,
  enableFieldEditing = true,
  isEmbedded = false,
  expandWidth,
  hideAllActions = false,
  searchKeyworkMatches,
  featuredClauses,
  currentGroupId,
  showGroupsTab = true,
  onClauseHighlight,
  onFieldHighlight,
  onHide,
  customTabs,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const {
    download = enabledByDefault,
    navigate = enabledByDefault,
    rename = enabledByDefault,
    next,
    previous,
    upload,
    delete: deleteAction = enabledByDefault,
    viewTicket = enabledByDefault,
  } = actionsConfig ?? {
    download: enabledByDefault,
    navigate: enabledByDefault,
    rename: enabledByDefault,
    delete: enabledByDefault,
  };
  const client = useClientSubDomain();
  const currentUser = useCurrentUser();
  const { navigate: routingNavigate } = useRouting();
  const docHandlerId = Array.isArray(docHandlerIdParam)
    ? docHandlerIdParam[0]
    : docHandlerIdParam;

  const { getInputProps, open } = useDropzone({
    onDrop: (files) => upload?.onSuccess?.(files[0]),
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
  });
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const hasEditClauseFlag = testHasFlag(FeatureFlagType.EditClause)(
    currentUser,
  );
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const hasAmendmentsFlag = useFlag(FlagType.WSSIntegrationM2Amendments);
  const hasDocumentDetailsPanelUpdate = useFlag(
    FlagType.DocumentDetailsPanelUpdate,
  );
  const pageSidebarContext = usePageSidebarContext();
  const reportPageSidebarWidth = useCallback(
    (w: string | number | undefined) => {
      if (!pageSidebarContext) return;
      const widthForSlot =
        isEmbedded && !hasDocumentDetailsPanelUpdate
          ? DOCUMENT_DETAILS_PANEL_WIDTH
          : w;
      pageSidebarContext.setEffectiveWidth(widthForSlot);
    },
    [pageSidebarContext, isEmbedded, hasDocumentDetailsPanelUpdate],
  );
  const [tab, setTab] = useState<Tabs>('fields');
  const [throttledPanelSearch, , handlePanelSearch] = useThrottledState(
    '',
    INPUT_DEBOUNCE_MS,
  );

  const { toast } = useToast();
  const { data: documentInfo } = api.endpoints.getDocumentInformation.useQuery(
    { documentId: docHandlerId!, formatDate, formatNumber },
    { skip: !docHandlerId },
  );
  const { data: ticketSummary, isFetching: isFetchingTicketSummary } =
    api.endpoints.getTicketSummary.useQuery(
      { ticketId: documentInfo?.ticketId ?? '' },
      { skip: !documentInfo?.ticketId },
    );
  const {
    data,
    isFetching: isFetchingDocument,
    refetch,
  } = api.endpoints.getDocumentHandler.useQuery(docHandlerId!, {
    skip: !docHandlerId,
  });
  const hasDownloadPermission =
    data?.visibilityLevel !== 'OPEN_NO_EDIT_DOWNLOAD';
  const canEdit = data?.visibilityLevel === 'OPEN';
  const effectiveEnableFieldEditing =
    enableFieldEditing && data?.docStatus !== 'TERMINATED';
  const endpoint = hasEditClauseFlag
    ? 'getAllClausesGrouped'
    : 'getDocumentDetailClauses';

  api.endpoints[endpoint].useQuery(
    hasEditClauseFlag
      ? { docId: docHandlerId!, page: 1, pageSize: 100 }
      : ({ documentId: docHandlerId! } as any),
    {
      skip: !docHandlerId,
    },
  );
  const [getDocumentOriginal, { isFetching: isFetchingDocumentOriginal }] =
    api.endpoints.getDocumentOriginal.useLazyQuery();

  const searchMatches = useMemo(() => {
    if (searchKeyworkMatches) {
      return searchKeyworkMatches.map((item) => ({
        content: [item],
        name: '',
      }));
    }
    return null;
  }, [searchKeyworkMatches]);

  async function defaultOnDownloadClick() {
    if (docHandlerId && data) {
      try {
        const doc = await getDocumentOriginal(docHandlerId).unwrap();
        if (doc) {
          saveAs(doc, `${data.documentName}.${data.fileType}`);
          trackSegment('selectDownloadDocument', {
            name: docHandlerId,
          });
          download.onSuccess?.();
        }
      } catch {
        toast({
          status: 'danger',
          message: intl.formatMessage(
            {
              id: 'DocumentDetailsPanel.downloadError',
              defaultMessage:
                'Something went wrong with downloading {documentName}',
              description:
                'Error message when download fails. Example: documentName = "Contract.pdf"',
            },
            { documentName: data.documentName },
          ),
        });
      }
    }
  }

  function defaultOnNavigateToDocClick() {
    openInNewTab(`/${client}/document/${docHandlerId}`);
    trackSegment('selectDocument', {
      name: docHandlerId,
    });
  }
  const tabs = {
    selectedTab: tab,
    onSelectTab: (tab: string) => setTab(tab as Tabs),
    enableContentPadding: false,
    tabs: [
      {
        label: intl.formatMessage({
          id: 'DocumentDetailsPanel.fieldsTab',
          defaultMessage: 'Fields',
          description: 'Tab label for fields',
        }),
        value: 'fields',
        panel: (
          <Fields
            enableFieldEditing={effectiveEnableFieldEditing}
            activeFieldId={activeFieldId}
            docHandlerId={docHandlerId}
            fileType={data?.fileType}
            onFieldHighlight={onFieldHighlight}
            search={
              hasDocumentDetailsPanelUpdate ? throttledPanelSearch : undefined
            }
          />
        ),
      },
      {
        label: intl.formatMessage({
          id: 'DocumentDetailsPanel.clausesTab',
          defaultMessage: 'Clauses',
          description: 'Tab label for clauses',
        }),
        value: 'clauses',
        panel: (
          <Clauses
            docHandlerId={docHandlerId}
            featuredClauses={featuredClauses}
            onClauseHighlight={onClauseHighlight}
            search={
              hasDocumentDetailsPanelUpdate ? throttledPanelSearch : undefined
            }
          />
        ),
      },
      ...(searchMatches
        ? [
            {
              label: intl.formatMessage({
                id: 'DocumentDetailsPanel.keywordMatchesTab',
                defaultMessage: 'Keyword Matches',
                description: 'Tab label for keyword matches',
              }),
              value: 'matches',
              panel: <SearchMatches matches={searchMatches!} />,
            },
          ]
        : []),
      ...(customTabs ?? []),
      ...(showGroupsTab && hasGroupedTicketAndDocs
        ? [
            {
              label: intl.formatMessage({
                id: 'DocumentDetailsPanel.groupsTab',
                defaultMessage: 'Groups',
                description: 'Tab label for groups',
              }),
              value: 'groups',
              panel: (
                <Layout pt={4} width="100%">
                  <GroupMembership
                    resourceType="document"
                    resourceId={data?.documentId?.toString() ?? ''}
                    currentGroup={currentGroupId}
                  />
                </Layout>
              ),
            },
          ]
        : []),
    ],
  };

  const actions: UserAction[] = [
    ...(!navigate.hidden
      ? [
          {
            icon: 'open-in-new' as const,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.openDocument',
              defaultMessage: 'Open Document',
              description: 'Tooltip for open document action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.openDocumentText',
              defaultMessage: 'Open Document',
              description: 'Text for open document action',
            }),
            onClick: navigate.onClick ?? defaultOnNavigateToDocClick,
            disabled: navigate.disabled,
          },
        ]
      : []),
    ...(upload && !upload.hidden && canEdit
      ? [
          {
            icon: 'upload' as const,
            disabled: upload.disabled,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.upload',
              defaultMessage: 'Upload',
              description: 'Tooltip for upload action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.uploadText',
              defaultMessage: 'Upload',
              description: 'Text for upload action',
            }),
            onClick: () => {
              if (upload.onClick) {
                upload.onClick();
              } else {
                open();
              }
              trackSegment('selectUploadNewVersion', {
                name: docHandlerId,
              });
            },
          },
        ]
      : []),
    ...(!download.hidden
      ? [
          {
            icon: 'download' as const,
            tooltip:
              !hasDownloadPermission && data?.id === docHandlerId
                ? intl.formatMessage({
                    id: 'DocumentDetailsPanel.noDownloadPermission',
                    defaultMessage:
                      'You do not have permission to download this document',
                    description: 'Tooltip when user lacks download permission',
                  })
                : intl.formatMessage({
                    id: 'DocumentDetailsPanel.download',
                    defaultMessage: 'Download',
                    description: 'Tooltip for download action',
                  }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.downloadText',
              defaultMessage: 'Download',
              description: 'Text for download action',
            }),
            onClick: download.onClick ?? defaultOnDownloadClick,
            disabled: download.disabled || !hasDownloadPermission,
            isLoading: isFetchingDocument || isFetchingDocumentOriginal,
          },
        ]
      : []),
    ...(previous && !previous.hidden
      ? [
          {
            disabled: previous.disabled,
            icon: 'chevron-left' as const,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.previous',
              defaultMessage: 'Previous',
              description: 'Tooltip for previous action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.previousText',
              defaultMessage: 'Previous',
              description: 'Text for previous action',
            }),
            onClick: () => {
              previous.onClick?.();
              trackSegment('selectPreviousDocument', {
                name: docHandlerId,
              });
            },
          },
        ]
      : []),
    ...(next && !next.hidden
      ? [
          {
            disabled: next.disabled,
            icon: 'chevron-right' as const,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.next',
              defaultMessage: 'Next',
              description: 'Tooltip for next action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.nextText',
              defaultMessage: 'Next',
              description: 'Text for next action',
            }),
            onClick: () => {
              next.onClick?.();
              trackSegment('selectNextDocument', {
                name: docHandlerId,
              });
            },
          },
        ]
      : []),
  ];

  const hasTicketViewPermission =
    ticketSummary?.permissions?.includes(TicketPermissionType.TicketView) ??
    false;
  const isViewTicketDisabled = viewTicket.disabled || !hasTicketViewPermission;

  const shouldDisableAmend =
    isFetchingTicketSummary || ticketSummary?.isAmendable !== true;

  const moreActions: UserAction[] = [
    ...(viewTicket && !viewTicket.hidden && documentInfo?.ticketId
      ? [
          {
            icon: 'view' as const,
            disabled: isViewTicketDisabled,
            tooltip: !hasTicketViewPermission
              ? intl.formatMessage({
                  id: 'DocumentDetailsPanel.noTicketPermission',
                  defaultMessage:
                    'You do not have permission to view this ticket',
                  description:
                    'Tooltip when user lacks permission to view ticket',
                })
              : intl.formatMessage({
                  id: 'DocumentDetailsPanel.viewTicket',
                  defaultMessage: 'View Ticket',
                  description: 'Tooltip for view ticket action',
                }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.viewTicketText',
              defaultMessage: 'View Ticket',
              description: 'Text for view ticket action',
            }),
            onClick: () => {
              const ticketUrl = `/${client}/workflow/tickets/${documentInfo.ticketId}`;
              openInNewTab(ticketUrl);
              trackSegment('selectViewTicket', {
                ticketId: documentInfo.ticketId,
                documentId: docHandlerId,
              });
            },
          },
        ]
      : []),
    ...(hasAmendmentsFlag && documentInfo?.isWssContract
      ? [
          {
            icon: 'plus' as const,
            rightIcon: isFetchingTicketSummary ? 'loading' : undefined,
            disabled: shouldDisableAmend,
            disabledReason: (() => {
              if (isFetchingTicketSummary) {
                return intl.formatMessage({
                  id: 'DocumentDetailsPanel.loadingTicketSummary',
                  defaultMessage: 'Loading ticket summary',
                  description:
                    'used as a tooltip, shown when customers hover a menu option and we are still fetching data',
                });
              }

              if (shouldDisableAmend) {
                return intl.formatMessage({
                  id: 'DocumentDetailsPanel.notAmendable',
                  defaultMessage: 'This is not the latest document',
                  description:
                    'used as a tooltip, shown when customers hover a menu option and the document is not the latest version. It explains why the option theyre trying to use is disabled.',
                });
              }
            })(),
            tooltip: shouldDisableAmend
              ? undefined
              : intl.formatMessage({
                  id: 'DocumentDetailsPanel.amend',
                  defaultMessage: 'Amend',
                  description: 'Tooltip for amend action',
                }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.amendText',
              defaultMessage: 'Amend',
              description: 'Text for amend action',
            }),
            onClick: () => {
              if (shouldDisableAmend) {
                return;
              }

              const amendPath = generatePath(
                RoutePathType.IntegrateWorkflowAmendIntakeForm,
                {
                  contractRecordUniqueId: ticketSummary.contractRecordUniqueId!,
                },
              );
              routingNavigate(amendPath);
              trackSegment('selectAmend', {
                ticketId: documentInfo?.ticketId,
                documentId: docHandlerId,
                contractRecordUniqueId: ticketSummary.contractRecordUniqueId,
              });
            },
          } as UserAction,
        ]
      : []),
    ...(rename && !rename.hidden && canEdit
      ? [
          {
            icon: 'edit' as const,
            disabled: rename.disabled,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.rename',
              defaultMessage: 'Rename',
              description: 'Tooltip for rename action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.renameText',
              defaultMessage: 'Rename',
              description: 'Text for rename action',
            }),
            onClick: () => {
              setIsRenameModalVisible(true);
              trackSegment('selectRenameDocument', {
                name: docHandlerId,
              });
            },
          },
        ]
      : []),
    ...(deleteAction && !deleteAction.hidden && canEdit
      ? [
          {
            disabled: deleteAction.disabled,
            tooltip: intl.formatMessage({
              id: 'DocumentDetailsPanel.delete',
              defaultMessage: 'Delete',
              description: 'Tooltip for delete action',
            }),
            text: intl.formatMessage({
              id: 'DocumentDetailsPanel.deleteText',
              defaultMessage: 'Delete',
              description: 'Text for delete action',
            }),
            onClick: () => {
              if (deleteAction.onClick) {
                deleteAction.onClick();
              } else {
                setIsDeleteModalVisible(true);
              }
              trackSegment('selectDeleteDocument', {
                name: docHandlerId,
              });
            },
          },
        ]
      : []),
  ];
  if (!Boolean(docHandlerId)) return null;
  return (
    <>
      {upload && !upload.hidden && (
        <input {...getInputProps()} style={{ display: 'none' }} />
      )}
      {isRenameModalVisible && docHandlerId && data && (
        <RenameActionModal
          document={{
            id: docHandlerId,
            folderId: data.path[data.path.length - 1].id,
            name: data.documentName,
          }}
          isVisible={isRenameModalVisible}
          onHide={() => setIsRenameModalVisible(false)}
          onSuccess={(newName: string) => {
            rename?.onSuccess?.(newName);
            refetch();
          }}
        />
      )}
      {isDeleteModalVisible && docHandlerId && data && (
        <DeleteActionModal
          isVisible={isDeleteModalVisible}
          documentIds={
            Array.isArray(docHandlerIdParam)
              ? docHandlerIdParam
              : [docHandlerId]
          }
          onHide={() => setIsDeleteModalVisible(false)}
          onSuccess={() => {
            deleteAction?.onSuccess?.();
            onHide();
          }}
        />
      )}
      <Panel
        position={!isEmbedded ? 'right' : undefined}
        width={
          !isEmbedded && !hasDocumentDetailsPanelUpdate
            ? 'l'
            : hasDocumentDetailsPanelUpdate
              ? DOCUMENT_DETAILS_PANEL_WIDTH
              : '100%'
        }
        loadingContent={{ isLoading: isFetchingDocument }}
        actions={hideAllActions ? undefined : actions}
        moreActions={hideAllActions ? undefined : moreActions}
        hidden={{ isHidden: false, onHide }}
        enableMaximize={hasDocumentDetailsPanelUpdate}
        expandWidth={expandWidth}
        onWidthChange={
          hasDocumentDetailsPanelUpdate
            ? pageSidebarContext
              ? reportPageSidebarWidth
              : undefined
            : undefined
        }
        title={documentName ?? data?.documentName ?? ''}
        tabs={tabs}
        search={
          hasDocumentDetailsPanelUpdate
            ? { onChange: handlePanelSearch }
            : undefined
        }
      />
    </>
  );
};

export default DocumentDetailsPanel;
